import {
	autoPlacement,
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { MutableRefObject, ReactNode, useEffect, useState } from 'react'
import { simpleExpandFromOrigin } from '../../animation'
import { WidgetWrapper } from './widget-wrapper'

export type FloatingWidgetChildrenHelpers = {
	active: boolean
	close: () => void
}

export interface FloatingWidgetProps {
	children: (helpers: FloatingWidgetChildrenHelpers) => ReactNode
	className?: string
	widget: (helpers: FloatingWidgetChildrenHelpers) => ReactNode
	label: ReactNode
	initialFocus?: MutableRefObject<HTMLElement | null>
	isDisabled?: boolean
	initialOpen?: boolean
}

export function FloatingWidget(
	{ children, className = '', widget, label, initialFocus, isDisabled = false, initialOpen = false }:
		FloatingWidgetProps,
) {
	const [isOpen, setIsOpen] = useState(initialOpen && !isDisabled)

	useEffect(() => {
		if (isDisabled) { setIsOpen(false) }
	}, [isDisabled])

	const { refs, floatingStyles, context, placement } = useFloating({
		open: isOpen && !isDisabled,
		onOpenChange: (state) => {
			if (!isDisabled) {
				setIsOpen(state)
			}
		},
		whileElementsMounted: autoUpdate,
		middleware: [autoPlacement()],
	})

	const click = useClick(context)
	const dismiss = useDismiss(context)

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

	return (
		<>
			<div ref={refs.setReference} {...getReferenceProps()} className={className}>
				{children({ active: isOpen, close: () => setIsOpen(false) })}
			</div>
			<AnimatePresence>
				{isOpen
					? (
						<FloatingFocusManager context={context} initialFocus={initialFocus}>
							<FloatingPortal>
								<div
									{...getFloatingProps()}
									ref={refs.setFloating}
									style={floatingStyles}
									className="p-2 z-50"
								>
									<motion.div {...simpleExpandFromOrigin(placement)} transition={{ duration: 0.1 }}>
										<WidgetWrapper>
											<div className="flex justify-end pr-1">
												<div className="font-medium text-grey-800">{label}</div>
												<div className="grow-1" />
												<button
													onClick={() => setIsOpen(false)}
													className="block cursor-pointer -m-3 p-3"
												>
													<CloseIcon />
												</button>
											</div>
											{widget({ active: isOpen, close: () => setIsOpen(false) })}
										</WidgetWrapper>
									</motion.div>
								</div>
							</FloatingPortal>
						</FloatingFocusManager>
					)
					: null}
			</AnimatePresence>
		</>
	)
}

function CloseIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={10} height={10} viewBox="0 0 10 10">
			<path
				d="M0.37265000000000004 0.015420000000000001C0.2866 0.03859 0.21895 0.07898 0.14897000000000002 0.14897000000000002C0.11258 0.18535000000000001 0.07089000000000001 0.23864000000000002 0.056319999999999995 0.26739C-0.018160000000000003 0.41438 -0.018340000000000002 0.58339 0.05584 0.7326699999999999C0.07708999999999999 0.7754300000000001 0.46708 1.17042 2.18837 2.8925L4.29489 5 2.18837 7.1075C0.46708 8.82958 0.07708999999999999 9.22457 0.05584 9.26733C-0.018340000000000002 9.41661 -0.018160000000000003 9.58562 0.056319999999999995 9.73261C0.09034 9.799750000000001 0.20024 9.909650000000001 0.26737 9.94367C0.41042 10.01616 0.58741 10.01634 0.7326900000000001 9.94415C0.77542 9.922920000000001 1.17089 9.53245 2.8925 7.81163L5 5.7051099999999995 7.1075 7.81163C8.82911 9.53245 9.22458 9.922920000000001 9.26731 9.94415C9.41259 10.01634 9.58958 10.01616 9.73263 9.94367C9.84802 9.8852 9.96861 9.727770000000001 9.985800000000001 9.61318C9.989360000000001 9.58943 9.99626 9.57 10.001140000000001 9.57C10.00601 9.57 10.00993 9.53288 10.00985 9.4875C10.00975 9.43784 10.00629 9.41038 10.001130000000002 9.41851C9.995320000000001 9.42769 9.99045 9.41786 9.98595 9.387830000000001C9.9823 9.36352 9.96574 9.31469 9.94915 9.27932C9.92097 9.21925 9.77989 9.07587 7.81205 7.1075L5.7051099999999995 5 7.81205 2.8925C9.77989 0.92413 9.92097 0.78075 9.94915 0.72068C9.96574 0.6853100000000001 9.98223 0.63693 9.985800000000001 0.61318C9.989360000000001 0.58943 9.99626 0.5700000000000001 10.001140000000001 0.5700000000000001C10.00601 0.5700000000000001 10.00993 0.53288 10.00985 0.4875C10.00975 0.43784 10.00629 0.41037999999999997 10.001130000000002 0.41851C9.995320000000001 0.42769 9.99045 0.41786 9.98595 0.38783C9.96861 0.27222999999999997 9.84849 0.11504 9.73261 0.056319999999999995C9.58562 -0.018160000000000003 9.41661 -0.018340000000000002 9.26733 0.05584C9.22457 0.07708999999999999 8.82958 0.46708 7.1075 2.18837L5 4.29489 2.8925 2.18837C0.64205 -0.061040000000000004 0.7580800000000001 0.05008 0.62307 0.014920000000000001C0.55232 -0.0035099999999999997 0.44211 -0.0032900000000000004 0.37265000000000004 0.015420000000000001M0.00349 0.5C0.00349 0.54675 0.005220000000000001 0.5658700000000001 0.00732 0.5425C0.00943 0.51912 0.00943 0.48087 0.00732 0.4575C0.005220000000000001 0.43412 0.00349 0.45325000000000004 0.00349 0.5M0.00349 9.5C0.00349 9.54675 0.005220000000000001 9.56587 0.00732 9.5425C0.00943 9.51913 0.00943 9.48087 0.00732 9.4575C0.005220000000000001 9.43413 0.00349 9.45325 0.00349 9.5"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.01}
			/>
		</svg>
	)
}
